@file:OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)

package com.example.monetix.ui.dialogs

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.DateRange
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.*
import com.example.monetix.ui.components.MonetixTextField as OutlinedTextField
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import coil3.compose.AsyncImage
import com.example.monetix.ui.components.ModernMoneyText
import com.example.monetix.ui.components.MonetixGlassSwitch
import com.example.monetix.ui.components.TxLogo
import com.example.monetix.utils.autoFaviconUrlForName
import com.example.monetix.utils.rememberMediaHandler
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlin.math.pow
import kotlin.math.roundToLong

// ==========================================
// 🚀 IL MOTORE SEMANTICO PER FINANZIARIE
// ==========================================
private data class PredefCreditor(
    val name: String,
    val emoji: String? = null,
    val customUrl: String? = null
)

private val defaultCreditorSuggestions = listOf(
    PredefCreditor("Findomestic", customUrl = "https://icon.horse/icon/findomestic.it"),
    PredefCreditor("Compass", customUrl = "https://icon.horse/icon/compass.it"),
    PredefCreditor("Agos", customUrl = "https://icon.horse/icon/agos.it"),
    PredefCreditor("Younited Credit", customUrl = "https://icon.horse/icon/younited-credit.com"),
    PredefCreditor("Intesa Sanpaolo", customUrl = "https://icon.horse/icon/intesasanpaolo.com"),
    PredefCreditor("UniCredit", customUrl = "https://icon.horse/icon/unicredit.it"),
    PredefCreditor("Santander", customUrl = "https://icon.horse/icon/santanderconsumer.it"),
    PredefCreditor("CA Auto Bank", customUrl = "https://icon.horse/icon/ca-autobank.it"),
    PredefCreditor("FCA Bank", customUrl = "https://icon.horse/icon/fcabank.it"),
    PredefCreditor("Sella Personal Credit", customUrl = "https://icon.horse/icon/sellapersonalcredit.it"),
    PredefCreditor("Banca", emoji = "🏦"),
    PredefCreditor("Concessionaria", emoji = "🚗"),
    PredefCreditor("Amico/Parente", emoji = "🤝")
)

private fun detectSmartCreditor(input: String): PredefCreditor? {
    val cleanInput = input.trim().lowercase()
    if (cleanInput.length < 3) return null
    return defaultCreditorSuggestions.firstOrNull { it.name.lowercase().contains(cleanInput) }
}

// 🚀 COMPONENTE CUSTOM: Cancella il testo al primo tocco!
@Composable
fun ClearOnFocusTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    keyboardType: KeyboardType = KeyboardType.Decimal
) {
    var wasFocused by remember { mutableStateOf(false) }

    OutlinedTextField(
        value = value,
        onValueChange = { newValue ->
            if (newValue.all { c -> c.isDigit() || c == '.' || c == ',' }) {
                onValueChange(newValue)
            }
        },
        label = { Text(label) },
        keyboardOptions = KeyboardOptions(keyboardType = keyboardType),
        modifier = modifier.onFocusChanged { focusState ->
            if (focusState.isFocused && !wasFocused) {
                onValueChange("")
            }
            wasFocused = focusState.isFocused
        },
        shape = RoundedCornerShape(14.dp),
        singleLine = true,
        colors = OutlinedTextFieldDefaults.colors(
            focusedTextColor = Color.White,
            unfocusedTextColor = Color.White,
            focusedContainerColor = Color.White.copy(alpha = 0.08f),
            unfocusedContainerColor = Color.White.copy(alpha = 0.08f),
            focusedBorderColor = Color(0xFFC084FC),
            unfocusedBorderColor = Color.White.copy(alpha = 0.15f),
            focusedLabelColor = Color(0xFFC084FC),
            unfocusedLabelColor = Color(0xFFA0A0A0)
        )
    )
}

// 🚀 POPUP RICERCA IMMAGINI WEB AUTOMATICA
@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun WebImagePickerPopup(
    initialQuery: String,
    onDismiss: () -> Unit,
    onImageSelected: (String) -> Unit
) {
    var query by remember { mutableStateOf(TextFieldValue(initialQuery)) }
    var results by remember { mutableStateOf<List<String>>(emptyList()) }
    val uriHandler = LocalUriHandler.current

    fun generateLinks(qRaw: String) {
        val q = qRaw.trim().lowercase().replace(" ", "")
        if (q.isEmpty()) { results = emptyList(); return }
        results = listOf(
            "https://logo.clearbit.com/$q.com",
            "https://logo.clearbit.com/$q.it",
            "https://icon.horse/icon/$q.com",
            "https://icon.horse/icon/$q.it",
            "https://www.google.com/s2/favicons?domain=$q.com&sz=128",
            "https://www.google.com/s2/favicons?domain=$q.it&sz=128"
        ).distinct()
    }

    LaunchedEffect(initialQuery) { if (initialQuery.isNotBlank()) generateLinks(initialQuery) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Ricerca Logo Web", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold)) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                OutlinedTextField(
                    value = query,
                    onValueChange = { query = it },
                    label = { Text("Nome brand o azienda") },
                    trailingIcon = { IconButton(onClick = { generateLinks(query.text) }) { Icon(Icons.Default.Search, contentDescription = "Cerca", tint = MaterialTheme.colorScheme.primary) } },
                    singleLine = true, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(14.dp)
                )

                if (results.isNotEmpty()) {
                    Text("Tocca il logo che preferisci:", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                    LazyVerticalGrid(
                        columns = GridCells.Adaptive(60.dp),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                        modifier = Modifier.height(180.dp).fillMaxWidth()
                    ) {
                        items(results) { url ->
                            Box(
                                modifier = Modifier.aspectRatio(1f).clip(RoundedCornerShape(14.dp)).background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f))
                                    .clickable { onImageSelected(url) }.border(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.2f), RoundedCornerShape(14.dp)),
                                contentAlignment = Alignment.Center
                            ) { AsyncImage(model = url, contentDescription = null, modifier = Modifier.fillMaxSize().padding(8.dp)) }
                        }
                    }
                } else {
                    Box(modifier = Modifier.height(180.dp).fillMaxWidth(), contentAlignment = Alignment.Center) {
                        Text("Scrivi un brand e premi la lente", color = MaterialTheme.colorScheme.onSurfaceVariant)
                    }
                }
                HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f))
                Button(
                    onClick = { uriHandler.openUri("https://www.google.com/search?tbm=isch&q=${query.text.replace(" ", "+")}+logo+icon") },
                    modifier = Modifier.fillMaxWidth(), colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.secondaryContainer, contentColor = MaterialTheme.colorScheme.onSecondaryContainer)
                ) {
                    Icon(Icons.Outlined.Search, contentDescription = null, modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Non lo trovi? Cerca su Google")
                }

                var manualUrl by remember { mutableStateOf("") }
                OutlinedTextField(
                    value = manualUrl, onValueChange = { manualUrl = it },
                    label = { Text("Incolla URL immagine qui", fontSize = 10.sp) },
                    singleLine = true, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(14.dp)
                )
                AnimatedVisibility(visible = manualUrl.isNotBlank()) {
                    Column(modifier = Modifier.fillMaxWidth().padding(top = 8.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                        Button(
                            onClick = { onImageSelected(manualUrl) },
                            colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.primary)
                        ) { Text("Usa questa immagine", fontWeight = FontWeight.Bold) }
                    }
                }
            }
        },
        confirmButton = { TextButton(onClick = onDismiss) { Text("CHIUDI", color = MaterialTheme.colorScheme.onSurfaceVariant) } }
    )
}

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun LoanSimulatorDialog(
    onDismiss: () -> Unit,
    onSaveAsFinancing: ((amountCents: Long, monthlyInstallmentCents: Long, commissionCents: Long, durationMonths: Int, startDateMillis: Long, taeg: Double, creditor: String, purpose: String, logoEmoji: String?, logoUri: String?) -> Unit)? = null
) {
    val brandPrimary = Color(0xFF60A5FA)
    val panelColor = Color.White.copy(alpha = 0.08f)

    // 🚀 STATI FINANZIARI
    var amountText by remember { mutableStateOf("10000") }
    var tanText by remember { mutableStateOf("6.5") }
    var taegManualText by remember { mutableStateOf("") }
    var istruttoriaText by remember { mutableStateOf("150") }
    var incassoRataText by remember { mutableStateOf("2.50") }
    var assicurazioneText by remember { mutableStateOf("450") }
    var durationText by remember { mutableStateOf("60") }
    var hasAssicurazione by remember { mutableStateOf(false) }

    // 🚀 STATI ANAGRAFICI & LOGO
    var creditorText by remember { mutableStateOf("") }
    var purposeText by remember { mutableStateOf("") }
    var accLogoEmoji by remember { mutableStateOf("") }
    var accLogoUri by remember { mutableStateOf("") }
    var manualLogoSet by remember { mutableStateOf(false) }

    var showEmojiInput by remember { mutableStateOf(false) }
    var showWebSearch by remember { mutableStateOf(false) }

    // LOGICA REATTIVA LOGO
    val currentCleanCreditor = creditorText.trim()
    val smartMatch = detectSmartCreditor(currentCleanCreditor)

    val finalEmojiToSave = if (manualLogoSet && accLogoEmoji.isNotBlank()) accLogoEmoji else smartMatch?.emoji
    val finalUriToSave = if (manualLogoSet && accLogoUri.isNotBlank()) accLogoUri else smartMatch?.customUrl ?: autoFaviconUrlForName(if (currentCleanCreditor.isNotBlank()) currentCleanCreditor else purposeText.ifBlank { "Banca" })

    val mediaHandler = rememberMediaHandler(
        onImageResult = { uri ->
            if (uri != null) {
                accLogoUri = uri.toString()
                accLogoEmoji = ""
                manualLogoSet = true
            }
        },
        onVoiceResult = { }
    )

    // GESTIONE DATE
    var showDatePicker by remember { mutableStateOf(false) }
    val initialDateMillis = remember { Clock.System.now().toEpochMilliseconds() }
    var startDateMillis by remember { mutableStateOf(initialDateMillis) }
    val datePickerState = rememberDatePickerState(initialSelectedDateMillis = startDateMillis)

    val startDateStr = remember(startDateMillis) {
        val startEpochDay = startDateMillis / 86400000L
        val start = LocalDate.fromEpochDays(startEpochDay.toInt())
        "${start.dayOfMonth.toString().padStart(2, '0')}/${start.monthNumber.toString().padStart(2, '0')}/${start.year}"
    }

    val endDateStr = remember(startDateMillis, durationText) {
        val months = durationText.toIntOrNull() ?: 0
        if (months > 0) {
            val startEpochDay = startDateMillis / 86400000L
            val start = LocalDate.fromEpochDays(startEpochDay.toInt())
            var m = start.monthNumber + months
            var y = start.year
            while (m > 12) { m -= 12; y++ }
            val d = minOf(start.dayOfMonth, 28)
            "${d.toString().padStart(2, '0')}/${m.toString().padStart(2, '0')}/$y"
        } else { "--/--/----" }
    }

    // 🚀 MOTORE DI CALCOLO KMP-Safe
    val simulationResult = remember(amountText, tanText, istruttoriaText, incassoRataText, hasAssicurazione, assicurazioneText, durationText, taegManualText) {
        val nettoErogato = amountText.replace(",", ".").toDoubleOrNull() ?: 0.0
        val tanAnnual = tanText.replace(",", ".").toDoubleOrNull() ?: 0.0
        val speseIstruttoria = istruttoriaText.replace(",", ".").toDoubleOrNull() ?: 0.0
        val commissioneBanca = incassoRataText.replace(",", ".").toDoubleOrNull() ?: 0.0
        val costoAssicurazione = if (hasAssicurazione) (assicurazioneText.replace(",", ".").toDoubleOrNull() ?: 0.0) else 0.0
        val months = durationText.toIntOrNull() ?: 0
        val taegUtente = taegManualText.replace(",", ".").toDoubleOrNull()

        val capitaleFinanziato = nettoErogato + speseIstruttoria + costoAssicurazione

        if (capitaleFinanziato > 0 && months > 0) {
            val monthlyRate = (tanAnnual / 100.0) / 12.0
            val rataMensilePura = if (tanAnnual > 0.0) {
                val mathPow = (1.0 + monthlyRate).pow(months.toDouble())
                capitaleFinanziato * (monthlyRate * mathPow) / (mathPow - 1.0)
            } else { capitaleFinanziato / months }
            val rataMensileTotale = rataMensilePura + commissioneBanca
            val totaleRestituireBanca = rataMensilePura * months
            val totaleCostiIncasso = commissioneBanca * months
            val totalInterest = totaleRestituireBanca - capitaleFinanziato

            var taegCalcolato = taegUtente ?: 0.0
            if (taegUtente == null && nettoErogato > 0 && rataMensilePura > 0) {
                var low = 0.0
                var high = 1.0
                var guess = 0.0
                for (i in 0..40) {
                    guess = (low + high) / 2
                    var npv = -nettoErogato
                    var discount = 1.0
                    val factor = 1.0 + guess
                    for (m in 1..months) { discount *= factor; npv += rataMensilePura / discount }
                    if (npv > 0) low = guess else high = guess
                }
                taegCalcolato = ((1.0 + guess).pow(12.0) - 1.0) * 100.0
            }

            mapOf(
                "rataPuraCents" to (rataMensilePura * 100).roundToLong(),
                "commissionCents" to (commissioneBanca * 100).roundToLong(),
                "rataTotaleCents" to (rataMensileTotale * 100).roundToLong(),
                "interessiCents" to (totalInterest * 100).roundToLong(),
                "totaleRestituireCents" to ((totaleRestituireBanca + totaleCostiIncasso) * 100).roundToLong(),
                "capitaleFinanziatoCents" to (capitaleFinanziato * 100).roundToLong(),
                "taeg" to taegCalcolato,
                "isTaegManual" to (taegUtente != null)
            )
        } else {
            mapOf("rataPuraCents" to 0L, "commissionCents" to 0L, "rataTotaleCents" to 0L, "interessiCents" to 0L, "totaleRestituireCents" to 0L, "capitaleFinanziatoCents" to 0L, "taeg" to 0.0, "isTaegManual" to false)
        }
    }

    val rataPuraCents = simulationResult["rataPuraCents"] as Long
    val commissionCents = simulationResult["commissionCents"] as Long
    val rataTotaleCents = simulationResult["rataTotaleCents"] as Long
    val interessiCents = simulationResult["interessiCents"] as Long
    val totaleRestituireCents = simulationResult["totaleRestituireCents"] as Long
    val capitaleFinanziatoCents = simulationResult["capitaleFinanziatoCents"] as Long
    val taeg = simulationResult["taeg"] as Double
    val isTaegManual = simulationResult["isTaegManual"] as Boolean

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .systemBarsPadding()
                .imePadding()
                .background(
                    Brush.verticalGradient(
                        colors = listOf(
                            Color(0xFF701A75).copy(alpha = 0.6f),
                            Color(0xFF1E1B4B),
                            Color(0xFF0F172A)
                        )
                    )
                )
        ) {
            Surface(
                modifier = Modifier.fillMaxSize(),
                color = Color.Transparent
            ) {
                Column(modifier = Modifier.fillMaxSize().systemBarsPadding()) {

                    // HEADER
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        IconButton(onClick = onDismiss) {
                            Icon(
                                imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                                contentDescription = "Indietro",
                                tint = Color.White
                            )
                        }
                    }

                    Text(
                        text = "Simulatore Prestito",
                        style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold),
                        color = Color.White,
                        modifier = Modifier.padding(start = 24.dp, end = 24.dp, bottom = 16.dp)
                    )

                    // CORPO SCORREVOLE
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .weight(1f)
                            .verticalScroll(rememberScrollState())
                            .padding(horizontal = 24.dp, vertical = 8.dp)
                            .padding(bottom = 80.dp), // padding for FAB
                        verticalArrangement = Arrangement.spacedBy(20.dp)
                    ) {

                        // SEZIONE LOGO E CREDITORE
                        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                            Text("LOGO E CREDITORE", style = MaterialTheme.typography.labelSmall, color = Color.Gray)

                            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                                TxLogo(
                                    logoEmoji = finalEmojiToSave,
                                    logoUri = finalUriToSave,
                                    name = if (currentCleanCreditor.isNotBlank()) currentCleanCreditor else purposeText.ifBlank { "Banca" },
                                    modifier = Modifier.size(54.dp).border(1.dp, Color.White.copy(alpha = 0.1f), CircleShape)
                                )

                                FlowRow(
                                    modifier = Modifier.weight(1f),
                                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                                    verticalArrangement = Arrangement.spacedBy(6.dp)
                                ) {
                                    OutlinedButton(onClick = { mediaHandler.launchGallery() }, modifier = Modifier.height(36.dp), shape = RoundedCornerShape(14.dp), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.2f)), contentPadding = PaddingValues(horizontal = 12.dp)) { Text("FOTO", style = MaterialTheme.typography.labelSmall, color = Color.White) }
                                    OutlinedButton(onClick = { showEmojiInput = !showEmojiInput; showWebSearch = false }, modifier = Modifier.height(36.dp), shape = RoundedCornerShape(14.dp), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.2f)), contentPadding = PaddingValues(horizontal = 12.dp)) { Text("EMOJI", style = MaterialTheme.typography.labelSmall, color = Color.White) }
                                    OutlinedButton(onClick = { showWebSearch = true; showEmojiInput = false }, modifier = Modifier.height(36.dp), shape = RoundedCornerShape(14.dp), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.2f)), contentPadding = PaddingValues(horizontal = 12.dp)) { Text("WEB", style = MaterialTheme.typography.labelSmall, color = Color.White) }
                                }
                            }

                            AnimatedVisibility(visible = showEmojiInput) {
                                OutlinedTextField(
                                    value = accLogoEmoji,
                                    onValueChange = {
                                        if(it.length <= 3) {
                                            accLogoEmoji = it
                                            accLogoUri = ""
                                            manualLogoSet = true
                                        }
                                    },
                                    label = { Text("Inserisci Emoji", color = Color(0xFFA0A0A0)) },
                                    singleLine = true, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(14.dp),
                                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.White.copy(alpha = 0.08f), unfocusedContainerColor = Color.White.copy(alpha = 0.08f), focusedBorderColor = brandPrimary, unfocusedBorderColor = Color.White.copy(alpha = 0.20f), focusedLabelColor = brandPrimary, unfocusedLabelColor = Color(0xFFA0A0A0))
                                )
                            }

                            OutlinedTextField(
                                value = creditorText,
                                onValueChange = { creditorText = it },
                                label = { Text("Ente Creditore (es. Agos, Banca)", color = Color(0xFFA0A0A0)) },
                                singleLine = true, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(14.dp),
                                colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.White.copy(alpha = 0.08f), unfocusedContainerColor = Color.White.copy(alpha = 0.08f), focusedBorderColor = brandPrimary, unfocusedBorderColor = Color.White.copy(alpha = 0.20f), focusedLabelColor = brandPrimary, unfocusedLabelColor = Color(0xFFA0A0A0))
                            )
                            OutlinedTextField(
                                value = purposeText,
                                onValueChange = { purposeText = it },
                                label = { Text("Causale / Scopo (es. Auto nuova)", color = Color(0xFFA0A0A0)) },
                                modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(14.dp), minLines = 2,
                                colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.White.copy(alpha = 0.08f), unfocusedContainerColor = Color.White.copy(alpha = 0.08f), focusedBorderColor = brandPrimary, unfocusedBorderColor = Color.White.copy(alpha = 0.20f), focusedLabelColor = brandPrimary, unfocusedLabelColor = Color(0xFFA0A0A0))
                            )
                        }

                        // 1. DATI BASE
                        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                            Text("DATI FINANZIARI", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                            ClearOnFocusTextField(value = amountText, onValueChange = { amountText = it }, label = "Importo netto (€)", modifier = Modifier.fillMaxWidth())
                            Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                                ClearOnFocusTextField(value = tanText, onValueChange = { tanText = it }, label = "TAN (%)", modifier = Modifier.weight(0.5f))
                                ClearOnFocusTextField(value = taegManualText, onValueChange = { taegManualText = it }, label = "TAEG Ufficiale (%)", modifier = Modifier.weight(0.5f))
                            }
                        }

                        // 2. SPESE (Finanziate e Bancarie)
                        Column {
                            Text("SPESE (Finanziate e Bancarie)", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                            Spacer(Modifier.height(8.dp))
                            Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                                ClearOnFocusTextField(value = istruttoriaText, onValueChange = { istruttoriaText = it }, label = "Istruttoria Pratica (€)", modifier = Modifier.weight(0.5f))
                                ClearOnFocusTextField(value = incassoRataText, onValueChange = { incassoRataText = it }, label = "Incasso Rata/Mese (€)", modifier = Modifier.weight(0.5f))
                            }

                            Spacer(Modifier.height(12.dp))
                            Surface(
                                modifier = Modifier.fillMaxWidth().clip(RoundedCornerShape(16.dp)), 
                                color = panelColor, 
                                border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f))
                            ) {
                                Column(modifier = Modifier.padding(16.dp)) {
                                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                        Text("Includi Polizza", style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold, color = Color.White)
                                        MonetixGlassSwitch(checked = hasAssicurazione, onCheckedChange = { hasAssicurazione = it }, activeColor = brandPrimary)
                                    }
                                    AnimatedVisibility(visible = hasAssicurazione) {
                                        ClearOnFocusTextField(value = assicurazioneText, onValueChange = { assicurazioneText = it }, label = "Premio Assicurativo (€)", modifier = Modifier.fillMaxWidth().padding(top = 12.dp))
                                    }
                                }
                            }
                        }

                        // 3. DATE
                        Column(modifier = Modifier.fillMaxWidth()) {
                            Text("TEMPISTICHE", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                            Spacer(Modifier.height(8.dp))

                            OutlinedTextField(
                                value = startDateStr, onValueChange = {}, readOnly = true, label = { Text("Data prima rata", color = Color(0xFFA0A0A0)) },
                                trailingIcon = { Icon(Icons.Default.DateRange, tint = Color.White, contentDescription = null) },
                                modifier = Modifier.fillMaxWidth().clickable { showDatePicker = true }, enabled = false,
                                colors = OutlinedTextFieldDefaults.colors(disabledTextColor = Color.White, disabledBorderColor = Color.White.copy(0.2f), disabledLabelColor = Color(0xFFA0A0A0), disabledContainerColor = Color.White.copy(alpha = 0.08f)),
                                shape = RoundedCornerShape(14.dp)
                            )

                            Spacer(Modifier.height(12.dp))
                            ClearOnFocusTextField(value = durationText, onValueChange = { if (it.all { c -> c.isDigit() }) durationText = it }, label = "Durata (Mesi)", keyboardType = KeyboardType.Number, modifier = Modifier.fillMaxWidth())

                            Spacer(Modifier.height(12.dp))
                            val standardDurations = listOf("12", "24", "36", "48", "60", "72", "84", "120")
                            FlowRow(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                standardDurations.forEach { month ->
                                    FilterChip(
                                        selected = durationText == month, onClick = { durationText = month },
                                        label = { Text("$month mesi", style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold)) },
                                        colors = FilterChipDefaults.filterChipColors(
                                            selectedContainerColor = Color.White, 
                                            selectedLabelColor = Color.Black,
                                            containerColor = Color.White.copy(alpha = 0.10f),
                                            labelColor = Color.White
                                        ),
                                        shape = RoundedCornerShape(14.dp),
                                        border = null
                                    )
                                }
                            }
                        }

                        // 4. RISULTATI DASHBOARD
                        Surface(modifier = Modifier.fillMaxWidth().clip(RoundedCornerShape(16.dp)), color = panelColor, border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f))) {
                            Column(modifier = Modifier.padding(20.dp)) {
                                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                    Column {
                                        Text("ESBORSO MENSILE", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                                        ModernMoneyText(cents = rataTotaleCents, hideBalances = false, color = Color.White, integerFontSize = 32.sp, decimalFontSize = 18.sp, symbolFontSize = 18.sp)
                                    }
                                    Surface(shape = RoundedCornerShape(10.dp), color = Color.White.copy(alpha = 0.12f), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.20f))) {
                                        Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp)) {
                                            Text(if(isTaegManual) "TAEG UFFICIALE" else "TAEG FINANZ.", style = MaterialTheme.typography.labelSmall, color = Color.LightGray)
                                            val taegRounded = (taeg * 100).roundToLong()
                                            val taegStr = "${taegRounded / 100}.${(taegRounded % 100).toString().padStart(2, '0')}%"
                                            Text(taegStr, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Black, color = Color.White)
                                        }
                                    }
                                }
                                Spacer(Modifier.height(16.dp))
                                HorizontalDivider(color = Color.White.copy(alpha = 0.1f))
                                Spacer(Modifier.height(16.dp))

                                @Composable
                                fun ResultRow(label: String, value: String, isBold: Boolean = false) {
                                    Row(modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp), horizontalArrangement = Arrangement.SpaceBetween) {
                                        Text(label, style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                                        Text(value, style = MaterialTheme.typography.bodyMedium.copy(fontWeight = if(isBold) FontWeight.Bold else FontWeight.Normal), color = if (isBold) Color.White else Color.LightGray)
                                    }
                                }

                                val rataPuraStr = "${rataPuraCents / 100},${(rataPuraCents % 100).toString().padStart(2, '0')} €"
                                val commissioneStr = "${commissionCents / 100},${(commissionCents % 100).toString().padStart(2, '0')} €"
                                val capStr = "${capitaleFinanziatoCents / 100},${(capitaleFinanziatoCents % 100).toString().padStart(2, '0')} €"
                                val intStr = "${interessiCents / 100},${(interessiCents % 100).toString().padStart(2, '0')} €"
                                val totStr = "${totaleRestituireCents / 100},${(totaleRestituireCents % 100).toString().padStart(2, '0')} €"

                                ResultRow("Di cui Rata Pura (Finanziaria)", rataPuraStr)
                                ResultRow("Di cui Incasso Rata (Tua Banca)", commissioneStr)
                                Spacer(Modifier.height(8.dp))
                                ResultRow("Capitale lordo finanziato", capStr)
                                ResultRow("Interessi puri", intStr)
                                ResultRow("Totale uscite stimate", totStr, isBold = true)
                                Spacer(Modifier.height(12.dp))
                                ResultRow("Data ultima rata stimata:", endDateStr, isBold = true)
                            }
                        }
                    }
                }
            }

            // FAB at Bottom Right (Moved outside of Surface to be directly in Box)
            if (onSaveAsFinancing != null && rataPuraCents > 0) {
                FloatingActionButton(
                    onClick = {
                        var safeUri = accLogoUri.takeIf { it.isNotBlank() }
                        var safeEmoji = accLogoEmoji.takeIf { it.isNotBlank() }

                        if (safeUri == null && safeEmoji == null) {
                            val match = detectSmartCreditor(creditorText)
                            if (match != null) { safeUri = match.customUrl; safeEmoji = match.emoji }
                        }

                        onSaveAsFinancing.invoke(
                            capitaleFinanziatoCents, rataPuraCents, commissionCents, (durationText.toIntOrNull() ?: 0),
                            startDateMillis, taeg, creditorText.trim(), purposeText.trim(), safeEmoji, safeUri
                        )
                        onDismiss()
                    },
                    modifier = Modifier
                        .align(Alignment.BottomEnd)
                        .padding(bottom = 56.dp, end = 24.dp) // 🚀 Sollevato per evitare le gesture di sistema
                        .navigationBarsPadding(),
                    containerColor = brandPrimary,
                    contentColor = Color.White,
                    shape = CircleShape
                ) {
                    Icon(Icons.Default.Check, contentDescription = "Salva", modifier = Modifier.size(28.dp))
                }
            }
        }
    }

    if (showDatePicker) {
        DatePickerDialog(
            onDismissRequest = { showDatePicker = false },
            confirmButton = {
                TextButton(onClick = { datePickerState.selectedDateMillis?.let { startDateMillis = it }; showDatePicker = false }) { Text("CONFERMA", fontWeight = FontWeight.Bold, color = brandPrimary) }
            },
            dismissButton = { TextButton(onClick = { showDatePicker = false }) { Text("ANNULLA", color = MaterialTheme.colorScheme.onSurfaceVariant) } }
        ) { DatePicker(state = datePickerState, title = { Text("Seleziona data prima rata", modifier = Modifier.padding(start = 24.dp, top = 24.dp)) }) }
    }

    if (showWebSearch) {
        WebImagePickerPopup(
            initialQuery = currentCleanCreditor.ifBlank { "banca" },
            onDismiss = { showWebSearch = false },
            onImageSelected = { url -> accLogoUri = url; accLogoEmoji = ""; manualLogoSet = true; showWebSearch = false }
        )
    }
}